/**
 * The shared route composition: identity, explanation, status, one primary act,
 * secondary acts, then content. It fetches nothing and grants nothing; hosts
 * supply already-governed words, actions and children.
 *
 * This is deliberately a recipe rather than another atom. The console had
 * independently restated `.page`, `.head` and `.title` across dozens of style
 * sheets. A page with a different business body should still begin with the
 * same calm hierarchy and responsive action grammar.
 */
import { useId, type ReactNode } from 'react'
import s from './PageShell.module.css'

export type PageWidth = 'content' | 'wide' | 'full'

export interface PageShellProps {
  title: ReactNode
  /** Routes default to one h1; embedded examples can preserve their heading order. */
  headingLevel?: 1 | 2 | 3
  eyebrow?: ReactNode
  summary?: ReactNode
  status?: ReactNode
  /** Secondary actions render before the single, visually final primary act. */
  secondaryActions?: ReactNode
  primaryAction?: ReactNode
  /** A restrained, high-emphasis identity band for a true landing surface. */
  appearance?: 'default' | 'feature'
  /** Compact governed facts that belong to that identity band. */
  highlights?: ReactNode
  width?: PageWidth
  testId?: string
  children: ReactNode
}

export function PageShell({
  title,
  headingLevel = 1,
  eyebrow,
  summary,
  status,
  secondaryActions,
  primaryAction,
  appearance = 'default',
  highlights,
  width = 'wide',
  testId,
  children,
}: PageShellProps) {
  const generatedTitleId = useId()
  const titleId = `page-title-${generatedTitleId.replace(/:/g, '')}`
  const hasActions = secondaryActions != null || primaryAction != null
  const Heading = `h${headingLevel}` as 'h1' | 'h2' | 'h3'

  return (
    <section className={s.page} data-width={width} data-testid={testId} aria-labelledby={titleId}>
      <header className={s.header} data-appearance={appearance}>
        <div className={s.identity}>
          {eyebrow != null && <div className={s.eyebrow}>{eyebrow}</div>}
          <div className={s.titleLine}>
            <Heading className={s.title} id={titleId}>{title}</Heading>
            {status != null && <div className={s.status}>{status}</div>}
          </div>
          {summary != null && <div className={s.summary}>{summary}</div>}
        </div>

        {hasActions && (
          <div className={s.actions} data-page-actions>
            {secondaryActions}
            {primaryAction}
          </div>
        )}
        {highlights != null && <div className={s.highlights} data-page-highlights>{highlights}</div>}
      </header>

      <div className={s.content}>{children}</div>
    </section>
  )
}
